import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowLeft, Tag, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { blogPosts } from "../data/blogPosts";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function renderContent(blocks) {
  return blocks.map((block, i) => {
    switch (block.type) {
      case "intro":
        return (
          <p key={i} className="text-lg text-gray-600 leading-relaxed mb-6 font-medium border-l-4 border-emerald-400 pl-5 py-1">
            {block.text}
          </p>
        );
      case "h2":
        return (
          <h2 key={i} className="text-2xl font-bold text-gray-900 mt-10 mb-4">
            {block.text}
          </h2>
        );
      case "h3":
        return (
          <h3 key={i} className="text-xl font-semibold text-gray-800 mt-7 mb-3">
            {block.text}
          </h3>
        );
      case "p":
        return (
          <p key={i} className="text-gray-600 leading-relaxed mb-5">
            {block.text}
          </p>
        );
      case "ul":
        return (
          <ul key={i} className="mb-6 space-y-2">
            {block.items.map((item, j) => (
              <li key={j} className="flex items-start gap-2.5 text-gray-600">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        );
      case "links":
        return (
          <div key={i} className="my-8 rounded-2xl border border-emerald-100 bg-emerald-50/50 p-5">
            {block.title && (
              <p className="text-sm font-bold text-emerald-800 uppercase tracking-wide mb-3">
                {block.title}
              </p>
            )}
            <ul className="space-y-2">
              {block.items.map((link, j) => (
                <li key={j}>
                  <Link
                    to={link.to}
                    className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold hover:text-emerald-900 hover:underline"
                  >
                    <ArrowRight size={15} />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      default:
        return null;
    }
  });
}

function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const postIndex = blogPosts.indexOf(post);
  const prevPost = postIndex > 0 ? blogPosts[postIndex - 1] : null;
  const nextPost = postIndex < blogPosts.length - 1 ? blogPosts[postIndex + 1] : null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.coverImage,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      "@type": "Organization",
      name: "Yoga Retreats Booking",
      url: "https://yogaretreatsbooking.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Yoga Retreats Booking",
      url: "https://yogaretreatsbooking.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://yogaretreatsbooking.com/blog/${post.slug}`,
    },
    keywords: post.keywords.join(", "),
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={post.title}
        description={post.excerpt}
        canonicalPath={`/blog/${post.slug}`}
        ogImagePath={post.coverImage}
        jsonLd={jsonLd}
      />

      {/* Cover image */}
      <div className="relative pt-16">
        <div className="h-72 sm:h-96 overflow-hidden">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 max-w-3xl mx-auto px-4 sm:px-6 pb-8">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="flex items-center gap-3 mb-3">
              <span className="inline-flex items-center gap-1 text-xs font-medium text-white bg-emerald-600/80 px-2.5 py-1 rounded-full backdrop-blur-sm">
                <Tag size={11} />
                {post.category}
              </span>
              <span className="text-xs text-white/80">{formatDate(post.date)}</span>
              <span className="flex items-center gap-1 text-xs text-white/80">
                <Clock size={12} />
                {post.readTime}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
              {post.title}
            </h1>
          </motion.div>
        </div>
      </div>

      {/* Article body */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">

        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-emerald-700 transition-colors mb-10 group"
        >
          <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
          Back to Blog
        </Link>

        {/* Content */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="prose-custom"
        >
          {renderContent(post.content)}
        </motion.div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
          {post.keywords.map((kw) => (
            <span
              key={kw}
              className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-full font-medium"
            >
              {kw}
            </span>
          ))}
        </div>

        {/* Prev / Next */}
        {(prevPost || nextPost) && (
          <div className="grid sm:grid-cols-2 gap-4 mt-12 pt-8 border-t border-gray-100">
            {prevPost ? (
              <Link
                to={`/blog/${prevPost.slug}`}
                className="flex flex-col gap-1 p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all group"
              >
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <ArrowLeft size={12} /> Previous
                </span>
                <span className="text-sm font-semibold text-gray-800 group-hover:text-emerald-700 transition-colors line-clamp-2">
                  {prevPost.title}
                </span>
              </Link>
            ) : (
              <div />
            )}
            {nextPost ? (
              <Link
                to={`/blog/${nextPost.slug}`}
                className="flex flex-col gap-1 p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all group text-right"
              >
                <span className="text-xs text-gray-400 flex items-center gap-1 justify-end">
                  Next <ArrowRight size={12} />
                </span>
                <span className="text-sm font-semibold text-gray-800 group-hover:text-emerald-700 transition-colors line-clamp-2">
                  {nextPost.title}
                </span>
              </Link>
            ) : (
              <div />
            )}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold text-white mb-2">Ready to Experience a Yoga Retreat?</h3>
          <p className="text-emerald-100 text-sm mb-5">
            Browse our hand-picked yoga and meditation retreats across India.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-white text-emerald-700 font-semibold text-sm hover:bg-emerald-50 transition-colors"
            >
              Browse Retreats
              <ArrowRight size={15} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500/50 text-white font-semibold text-sm border border-emerald-400/50 hover:bg-emerald-500 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlogPost;
